import { Suspense, lazy, useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FilterBar, rangeToFilters, type FilterOption } from '../components/analytics/FilterBar'
import { StatTiles, type Depth } from '../components/analytics/StatTiles'
import { ActionTable } from '../components/analytics/ActionTable'
import { SeriesChart } from '../components/analytics/SeriesChart'
import { BreakdownTable } from '../components/analytics/BreakdownTable'
import { actionLabel } from '../components/analytics/actionLabel'
import { formatScreen } from '../components/analytics/screen'
import {
  labelForSeed, labelForShare, labelForWorkspace,
  useActions, useActivityLog, useBreakdown, useDimensionValues, useEventFeed, useGuideAnalytics,
  useLocations, useSummary, useTimeseries,
  type ActivityRow, type AnalyticsFilters, type GroupKey,
} from '../hooks/useAnalytics'
// Lazily loaded: the map engine is several hundred KB and only the People view
// needs it, so it stays out of the chunk that renders every other page.
const OriginMap = lazy(() => import('../components/analytics/OriginMap'))

/**
 * The analytics dashboard.
 *
 * Previously analytics existed only as a strip at the bottom of a share page,
 * scoped by reconstructing the share from its members' email addresses and
 * aggregated in the browser over at most 1,000 rows. This is the dedicated
 * surface: one filter contract, server-side aggregates, and every dimension the
 * events actually carry.
 */

type ViewKey =
  | 'overview' | 'activity' | 'timeline' | 'apps' | 'people' | 'guides'
  | 'pages' | 'versions' | 'explorer'

/**
 * Two depths, one dashboard.
 *
 * Most of the people who read this are not engineers. The default has to answer
 * "what are our clients doing and how is it going for them" without ever
 * showing them a URL or a status code — so the request-level views are behind a
 * switch rather than removed, because when something looks wrong those views
 * are exactly what you need next.
 */
const VIEWS: Array<{ key: ViewKey; label: string; depth: Depth }> = [
  { key: 'overview', label: 'Overview', depth: 'business' },
  { key: 'activity', label: 'What they did', depth: 'business' },
  // Keyed `timeline`, not `activity`: that key already names "What they did" in
  // every link anyone has shared.
  { key: 'timeline', label: 'Activity', depth: 'business' },
  { key: 'apps', label: 'Apps', depth: 'business' },
  { key: 'people', label: 'People', depth: 'business' },
  { key: 'guides', label: 'Guides', depth: 'business' },
  { key: 'pages', label: 'Pages & API', depth: 'technical' },
  { key: 'versions', label: 'Versions', depth: 'technical' },
  { key: 'explorer', label: 'Explorer', depth: 'technical' },
]

const viewsFor = (depth: Depth) =>
  depth === 'technical' ? VIEWS : VIEWS.filter(v => v.depth === 'business')

interface UrlState {
  filters: AnalyticsFilters
  updateFilters: (next: Partial<AnalyticsFilters>) => void
  view: ViewKey
  setView: (v: ViewKey) => void
  depth: Depth
  setDepth: (d: Depth) => void
}

/** Filters live in the URL, so every view is deep-linkable and shareable. */
function useUrlFilters(): UrlState {
  const [params, setParams] = useSearchParams()

  const filters = useMemo<AnalyticsFilters>(() => {
    const defaults = rangeToFilters(30)
    const num = (key: string) => {
      const raw = params.get(key)
      const n = raw ? Number(raw) : NaN
      return Number.isInteger(n) && n > 0 ? n : undefined
    }
    // Multi-value filters live in the URL as one repeated param per choice, so
    // a filtered view stays shareable however many things are selected.
    const many = (key: string) => {
      const values = params.getAll(key).flatMap(v => v.split(',')).filter(Boolean)
      return values.length ? values : undefined
    }
    return {
      from: params.get('from') ?? defaults.from,
      to: params.get('to') ?? defaults.to,
      seed: params.get('seed') ?? undefined,
      shareId: num('shareId'),
      workspaceId: num('workspaceId'),
      version: many('version'),
      app: many('app'),
      email: many('email'),
      country: many('country'),
      device: many('device'),
      interaction: many('interaction'),
      // ON by default: the operator-facing question is "what are CLIENTS
      // doing", and farmer1st.org traffic swamps that — one internal user
      // accounted for more events on gcb than every client combined. Unticking
      // is recorded explicitly as `false`, otherwise it would round-trip
      // straight back to the default.
      excludeInternal: params.get('excludeInternal') !== 'false',
    }
  }, [params])

  // Business unless asked otherwise, and the technical views simply do not
  // exist at that depth — so a link shared with a non-engineer opens on the
  // view they can read.
  const depth: Depth = params.get('depth') === 'technical' ? 'technical' : 'business'
  const requested = (params.get('view') as ViewKey) || 'overview'
  const available = viewsFor(depth)
  const view = available.some(v => v.key === requested) ? requested : 'overview'

  const update = useCallback((next: Partial<AnalyticsFilters>) => {
    setParams(prev => {
      const out = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(next)) {
        out.delete(key)
        if (value === undefined || value === null || value === '') continue
        if (Array.isArray(value)) {
          for (const item of value) out.append(key, String(item))
        } else {
          // Booleans are written even when false — a filter that defaults to on
          // needs "off" to survive a reload.
          out.set(key, String(value))
        }
      }
      return out
    }, { replace: true })
  }, [setParams])

  const setView = useCallback((v: ViewKey) => {
    setParams(prev => {
      const out = new URLSearchParams(prev)
      out.set('view', v)
      return out
    }, { replace: true })
  }, [setParams])

  const setDepth = useCallback((d: Depth) => {
    setParams(prev => {
      const out = new URLSearchParams(prev)
      if (d === 'technical') out.set('depth', 'technical')
      else {
        out.delete('depth')
        // Leaving technical while standing on a technical view would show an
        // empty page; fall back rather than silently blanking.
        const current = out.get('view') as ViewKey | null
        if (current && !viewsFor('business').some(v => v.key === current)) out.set('view', 'overview')
      }
      return out
    }, { replace: true })
  }, [setParams])

  return { filters, updateFilters: update, view, setView, depth, setDepth }
}

/** Options for the filter bar, derived from what the data actually contains. */
function useFilterOptions(filters: AnalyticsFilters) {
  // Unscoped by seed/share so the selects don't empty themselves as you drill.
  const base = useMemo(() => ({ from: filters.from, to: filters.to }), [filters.from, filters.to])
  const seeds = useBreakdown(base, 'seed', undefined, 100)
  const shares = useBreakdown({ ...base, seed: filters.seed }, 'share', undefined, 100)
  const workspaces = useBreakdown({ ...base, seed: filters.seed, shareId: filters.shareId }, 'workspace', undefined, 100)
  const scoped = useMemo(
    () => ({ ...base, seed: filters.seed, shareId: filters.shareId }),
    [base, filters.seed, filters.shareId])
  const apps = useDimensionValues(scoped, 'app')
  const versions = useDimensionValues(scoped, 'version')
  const users = useDimensionValues(scoped, 'user')
  const devices = useDimensionValues(scoped, 'device')
  const platforms = useDimensionValues(scoped, 'platform')
  const countries = useDimensionValues(scoped, 'country')

  const labels = seeds.labels
  // Event counts as a hint, so the busiest option is obvious without reading
  // the whole list — which is the point of a searchable select.
  const withCounts = (values: Array<{ value: string; events: number }>) =>
    values.map(v => ({ value: v.value, label: v.value, hint: v.events.toLocaleString() }))

  return {
    labels: { ...labels, ...shares.labels, ...workspaces.labels },
    shareLabels: shares.labels,
    workspaceLabels: workspaces.labels,
    seedOptions: seeds.rows
      .filter(r => r.key !== null)
      .map<FilterOption>(r => ({ value: String(r.key), label: labelForSeed(seeds.labels, r.key) })),
    shareOptions: shares.rows
      .filter(r => r.key !== null)
      .map<FilterOption>(r => ({ value: String(r.key), label: labelForShare(shares.labels, r.key) })),
    workspaceOptions: workspaces.rows
      .filter(r => r.key !== null)
      .map<FilterOption>(r => ({ value: String(r.key), label: labelForWorkspace(workspaces.labels, r.key) })),
    appOptions: withCounts(apps.values),
    versionOptions: withCounts(versions.values),
    userOptions: users.values.map(v => ({
      value: v.value,
      label: v.value === '_magic_link' ? 'Anonymous link' : v.value,
      hint: v.events.toLocaleString(),
    })),
    deviceOptions: withCounts(devices.values),
    platformOptions: withCounts(platforms.values),
    countryOptions: withCounts(countries.values),
  }
}

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b px-5 py-3">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
      </CardHeader>
      {children}
    </Card>
  )
}

export default function AnalyticsPage() {
  const { filters, updateFilters, view, setView, depth, setDepth } = useUrlFilters()
  const options = useFilterOptions(filters)
  const summary = useSummary(filters)

  return (
    <div className="p-6">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h1 className="text-xl font-semibold text-foreground">Analytics</h1>
        <div className="flex items-center gap-3">
          {summary.error && <span className="text-sm text-destructive">{summary.error}</span>}
          <div className="flex rounded-md border p-0.5" role="group" aria-label="Detail level">
            {(['business', 'technical'] as Depth[]).map(d => (
              <button
                key={d}
                onClick={() => setDepth(d)}
                aria-pressed={depth === d}
                className={`rounded px-3 py-1 text-xs transition-colors ${
                  depth === d
                    ? 'bg-seed-green-500 font-medium text-white'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {d === 'business' ? 'What clients do' : 'Technical'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={updateFilters}
        seeds={options.seedOptions}
        shares={options.shareOptions}
        workspaces={options.workspaceOptions}
        apps={options.appOptions}
        versions={options.versionOptions}
        users={options.userOptions}
        devices={options.deviceOptions}
        platforms={options.platformOptions}
        countries={options.countryOptions}
      />

      <StatTiles summary={summary.data} loading={summary.loading} depth={depth} />

      <div className="mb-6 flex flex-wrap gap-1 border-b">
        {viewsFor(depth).map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              view === v.key
                ? 'border-seed-green-500 font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && <OverviewView filters={filters} options={options} onDrill={updateFilters} depth={depth} />}
      {view === 'activity' && <ActivityView filters={filters} depth={depth} />}
      {view === 'timeline' && <TimelineView filters={filters} onDrill={updateFilters} depth={depth} />}
      {view === 'apps' && <AppsView filters={filters} options={options} />}
      {view === 'pages' && <PagesView filters={filters} />}
      {view === 'versions' && <VersionsView filters={filters} />}
      {view === 'people' && <PeopleView filters={filters} onDrill={updateFilters} depth={depth} />}
      {view === 'guides' && <GuidesView filters={filters} />}
      {view === 'explorer' && <ExplorerView filters={filters} options={options} />}
    </div>
  )
}

type Options = ReturnType<typeof useFilterOptions>

function OverviewView({ filters, options, onDrill, depth }: {
  filters: AnalyticsFilters
  options: Options
  onDrill: (next: Partial<AnalyticsFilters>) => void
  depth: Depth
}) {
  const series = useTimeseries(filters, 'day')
  const seeds = useBreakdown(filters, 'seed', undefined, 20)
  const shares = useBreakdown(filters, 'share', undefined, 20)
  // The five things people did most, right on the overview: the question
  // "what are our clients doing" should be answered before anyone clicks.
  const top = useActions(filters, { limit: 5 })

  return (
    <div className="space-y-6">
      <Panel title="Activity" hint="daily · one interaction counted once, not its fan-out">
        <CardContent className="p-5">
          <SeriesChart series={series.series} metric="interactions" />
        </CardContent>
      </Panel>

      {depth === 'business' && (
        <Panel title="Most of what people did" hint="top 5 — the rest are under 'What they did'">
          <ActionTable rows={top.rows} loading={top.loading} />
        </Panel>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Seeds">
          <BreakdownTable
            rows={seeds.rows}
            loading={seeds.loading}
            keyHeader="Seed"
            labelFor={k => labelForSeed(seeds.labels, k)}
            onSelect={k => onDrill({ seed: k === null ? undefined : String(k), shareId: undefined, workspaceId: undefined })}
          />
        </Panel>
        <Panel title="Shares" hint="rows with no share are pre-cutover history">
          <BreakdownTable
            rows={shares.rows}
            loading={shares.loading}
            keyHeader="Share"
            labelFor={k => labelForShare(options.shareLabels, k)}
            onSelect={k => onDrill({ shareId: k === null ? undefined : Number(k), workspaceId: undefined })}
          />
        </Panel>
      </div>
    </div>
  )
}

/**
 * What they did.
 *
 * The answer to "the analytics is basically not really useful in this current
 * shape": a ranked list of things people did, in words, instead of a ranked
 * list of URLs. Ranked by collapsed activity, so the top of the list is what
 * most people did rather than whatever polls hardest.
 */
function ActivityView({ filters, depth }: { filters: AnalyticsFilters; depth: Depth }) {
  // `all` also lists the endpoints classified as plumbing — you cannot confirm
  // that a poll is a poll, or rescue a screen load misread as one, from a list
  // that hides them.
  const [showAll, setShowAll] = useState(false)
  const actions = useActions(filters, {
    scope: showAll ? 'all' : 'activity',
    includeHidden: showAll,
    limit: 200,
  })

  const namedShare = actions.totalActivity > 0
    ? 1 - actions.unnamedActivity / actions.totalActivity
    : 1

  return (
    <div className="space-y-6">
      {actions.unnamed > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
            <div>
              <p className="text-sm text-foreground">
                {actions.unnamed} of these are still described by a guess from the URL.
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {(namedShare * 100).toFixed(0)}% of activity in this range has a real name.
                Naming one applies to its whole history, not just from now on.
              </p>
            </div>
            <span className="h-1.5 w-40 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-seed-green-500"
                style={{ width: `${Math.round(namedShare * 100)}%` }}
              />
            </span>
          </CardContent>
        </Card>
      )}

      <Panel
        title={showAll ? 'Every endpoint' : 'What people did'}
        hint={showAll ? 'including plumbing and hidden rows' : 'repeats within a minute counted once'}
      >
        <div className="flex items-center justify-between border-b px-5 py-2.5">
          <span className="text-xs text-muted-foreground">
            {actions.rows.length} {showAll ? 'endpoints' : 'things'}
            {actions.truncated && ' (the busiest — there are more)'}
          </span>
          <Button size="sm" variant="outline" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Only what people did' : 'Show plumbing too'}
          </Button>
        </div>
        <ActionTable
          rows={actions.rows}
          loading={actions.loading}
          editable
          showRoutes={showAll}
          showRequests={showAll || depth === 'technical'}
          onSaved={actions.refetch}
          emptyMessage={showAll ? 'Nothing recorded in this range' : 'No activity in this range'}
        />
      </Panel>
    </div>
  )
}

const dayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })

/** A cell that narrows the whole dashboard to its value. */
function DrillCell({ label, title, onClick }: { label: string; title?: string; onClick?: () => void }) {
  if (!onClick) return <TableCell className="whitespace-nowrap text-muted-foreground">{label}</TableCell>
  return (
    <TableCell className="whitespace-nowrap text-muted-foreground">
      <button
        type="button"
        title={title}
        onClick={onClick}
        className="max-w-[14rem] truncate text-left hover:text-foreground hover:underline"
      >
        {label}
      </button>
    </TableCell>
  )
}

/**
 * Activity: who did what, and when.
 *
 * Every other view aggregates; this is the record underneath them. One row is
 * one unit of the counts everywhere else — repeats within a minute and plumbing
 * are left out the same way — so a number that looks wrong can be read row by
 * row. Clicking a seed, share, version, user or app narrows the dashboard to it.
 */
function TimelineView({ filters, onDrill, depth }: {
  filters: AnalyticsFilters
  onDrill: (next: Partial<AnalyticsFilters>) => void
  depth: Depth
}) {
  const [showAll, setShowAll] = useState(false)
  const log = useActivityLog(filters, { scope: showAll ? 'all' : 'activity' })
  const showRoutes = showAll || depth === 'technical'

  // Day headings instead of a date on every row: a log is read as a sequence,
  // and "which day" only changes a handful of times per page.
  const grouped = useMemo(() => {
    const out: Array<{ day: string; rows: ActivityRow[] }> = []
    for (const row of log.rows) {
      const day = dayFormat.format(row.ts)
      if (out.length === 0 || out[out.length - 1].day !== day) out.push({ day, rows: [] })
      out[out.length - 1].rows.push(row)
    }
    return out
  }, [log.rows])

  return (
    <Panel
      title={showAll ? 'Everything recorded' : 'Activity'}
      hint={showAll ? 'including repeats, plumbing and hidden endpoints' : 'newest first · repeats within a minute counted once'}
    >
      <div className="flex items-center justify-between border-b px-5 py-2.5">
        <span className="text-xs text-muted-foreground">
          {log.rows.length.toLocaleString()} {showAll ? 'events' : 'things done'}
          {log.exhausted ? ' — all of them' : ' loaded'}
        </span>
        <Button size="sm" variant="outline" onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Only what people did' : 'Show repeats and plumbing'}
        </Button>
      </div>

      {log.loading && log.rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : log.rows.length === 0 && !log.error ? (
        <div className="p-8 text-center text-sm text-muted-foreground">No activity in this range</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Seed</TableHead>
              <TableHead>Share</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Screen</TableHead>
              <TableHead>App</TableHead>
              <TableHead>What</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grouped.map(group => [
              <TableRow key={`day-${group.day}`} className="hover:bg-transparent">
                <TableCell colSpan={8} className="bg-muted/40 py-1.5 text-xs font-medium text-muted-foreground">
                  {group.day}
                </TableCell>
              </TableRow>,
              ...group.rows.map(row => {
                const user = row.email === '_magic_link' ? 'Anonymous link' : row.email
                const background = row.activityKind !== 'view' && row.activityKind !== 'action'
                const screen = formatScreen(row.screen)
                return (
                  <TableRow key={row.id} className={row.repeat || background || row.hidden ? 'opacity-60' : undefined}>
                    <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground" title={new Date(row.ts).toISOString()}>
                      {timeFormat.format(row.ts)}
                    </TableCell>
                    <DrillCell
                      label={row.seed ? labelForSeed(log.labels, row.seed) : '—'}
                      onClick={row.seed ? () => onDrill({ seed: row.seed!, shareId: undefined, workspaceId: undefined }) : undefined}
                    />
                    <DrillCell
                      label={row.shareId === null ? '—' : labelForShare(log.labels, row.shareId)}
                      onClick={row.shareId === null ? undefined : () => onDrill({ shareId: row.shareId!, workspaceId: undefined })}
                    />
                    <DrillCell
                      label={row.version ?? '—'}
                      onClick={row.version ? () => onDrill({ version: [row.version!] }) : undefined}
                    />
                    <DrillCell
                      label={user ?? '—'}
                      title={user ?? undefined}
                      onClick={row.email ? () => onDrill({ email: [row.email!] }) : undefined}
                    />
                    <TableCell
                      className={`whitespace-nowrap tabular-nums ${screen.inherited ? 'text-muted-foreground' : ''}`}
                      title={screen.title ?? undefined}
                    >
                      <div>{screen.primary}</div>
                      {screen.estimate && <div className="text-xs">{screen.estimate}</div>}
                      {screen.secondary && <div className="text-xs text-muted-foreground">{screen.secondary}</div>}
                    </TableCell>
                    <DrillCell
                      label={row.app ?? '—'}
                      onClick={row.app ? () => onDrill({ app: [row.app!] }) : undefined}
                    />
                    <TableCell className="max-w-[28rem]">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-foreground">
                          {actionLabel(row.method, row.pathTemplate ?? row.path, row.label)}
                        </span>
                        {row.repeat && <Badge variant="secondary" className="shrink-0 text-[10px]">repeat</Badge>}
                        {background && <Badge variant="secondary" className="shrink-0 text-[10px]">{row.activityKind ?? row.kind}</Badge>}
                        {row.hidden && <Badge variant="secondary" className="shrink-0 text-[10px]">hidden</Badge>}
                      </div>
                      {showRoutes && (
                        <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                          {row.method} {row.path}{row.status !== null ? ` · ${row.status}` : ''}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              }),
            ])}
          </TableBody>
        </Table>
      )}

      <div className="border-t p-4 text-center">
        {log.error && <p className="mb-2 text-sm text-destructive">{log.error}</p>}
        {log.exhausted
          ? log.rows.length > 0 && <span className="text-sm text-muted-foreground">End of results</span>
          : <Button variant="outline" size="sm" disabled={log.loading} onClick={log.loadMore}>
              {log.loading ? 'Loading…' : 'Load more'}
            </Button>}
      </div>
    </Panel>
  )
}

function AppsView({ filters, options }: { filters: AnalyticsFilters; options: Options }) {
  // Two dimensions: which cohort actually uses which app — the question the
  // single-dimension breakdown could never answer.
  const byApp = useBreakdown(filters, 'app', undefined, 50)
  const appByShare = useBreakdown(filters, 'app', 'share', 200)
  const series = useTimeseries(filters, 'day', 'app')

  return (
    <div className="space-y-6">
      <Panel title="App usage over time">
        <CardContent className="p-5">
          <SeriesChart series={series.series} metric="interactions" grouped labelFor={k => k} />
        </CardContent>
      </Panel>
      <Panel title="Apps">
        <BreakdownTable rows={byApp.rows} loading={byApp.loading} keyHeader="App" />
      </Panel>
      <Panel title="App adoption by share">
        <BreakdownTable
          rows={appByShare.rows}
          loading={appByShare.loading}
          keyHeader="App"
          subHeader="Share"
          subLabelFor={k => labelForShare(options.shareLabels, k)}
        />
      </Panel>
    </div>
  )
}

function PagesView({ filters }: { filters: AnalyticsFilters }) {
  const navPaths = useBreakdown({ ...filters, kind: ['nav'] }, 'path', undefined, 50)
  const apiPaths = useBreakdown({ ...filters, kind: ['api'] }, 'path', undefined, 50)

  return (
    <div className="space-y-6">
      <Panel title="Pages" hint="navigation only — assets excluded">
        <BreakdownTable rows={navPaths.rows} loading={navPaths.loading} keyHeader="Path" />
      </Panel>
      <Panel title="API endpoints" hint="error rate over calls with a recorded status">
        <BreakdownTable rows={apiPaths.rows} loading={apiPaths.loading} keyHeader="Endpoint" showErrors />
      </Panel>
    </div>
  )
}

function VersionsView({ filters }: { filters: AnalyticsFilters }) {
  const series = useTimeseries(filters, 'day', 'version')
  const byVersion = useBreakdown(filters, 'version', undefined, 50)

  return (
    <div className="space-y-6">
      <Panel title="Version adoption" hint="traffic per deployed version">
        <CardContent className="p-5">
          <SeriesChart series={series.series} metric="interactions" grouped labelFor={k => (k === '—' ? 'Unknown' : k)} />
          <p className="mt-4 text-xs text-muted-foreground">
            Version was never recorded before the analytics rewrite, and
            <code className="mx-1">seed_versions</code> cascades on seed delete, so events
            before 2026-05-04 and all events on deleted seeds report no version.
          </p>
        </CardContent>
      </Panel>
      <Panel title="Versions">
        <BreakdownTable rows={byVersion.rows} loading={byVersion.loading} keyHeader="Version" showErrors />
      </Panel>
    </div>
  )
}

function relativeTime(ts: number | null | undefined): string {
  if (!ts) return '—'
  const mins = Math.floor((Date.now() - Number(ts)) / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days < 30 ? `${days}d ago` : `${(days / 30.44).toFixed(1)}mo ago`
}

/**
 * Who, and from where.
 *
 * Two cards side by side rather than four stacked: the list answers "who is
 * using this" and the map answers "where from", and they are read together.
 * Device, browser and organisation move into the list as per-user columns —
 * they were never interesting as standalone rankings, only as attributes of a
 * person.
 */
function PeopleView({ filters, onDrill, depth }: {
  filters: AnalyticsFilters
  onDrill: (next: Partial<AnalyticsFilters>) => void
  depth: Depth
}) {
  // The request count sits beside interactions only for engineers — shown as
  // "283 /4,944" it read as a score nobody could decode.
  const showRequests = depth === 'technical'
  const users = useBreakdown(filters, 'user', undefined, 200)
  const userDevice = useBreakdown(filters, 'user', 'platform', 400)
  const userCountry = useBreakdown(filters, 'user', 'country', 400)
  const locations = useLocations(filters)

  // Breakdown rows arrive ordered by event count, so the first pairing for a
  // user is their most-used device and their most-common origin.
  const dominant = (rows: typeof userDevice.rows) => {
    const byUser = new Map<string, string>()
    for (const row of rows) {
      if (row.key === null || row.subKey === null || row.subKey === '') continue
      if (!byUser.has(String(row.key))) byUser.set(String(row.key), String(row.subKey))
    }
    return byUser
  }
  const deviceByUser = useMemo(() => dominant(userDevice.rows), [userDevice.rows])
  const countryByUser = useMemo(() => dominant(userCountry.rows), [userCountry.rows])

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Panel title="Users" hint="interactions, not requests">
        {users.loading && users.rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : users.rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No users in this range</div>
        ) : (
          <div className="max-h-[26rem] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Interactions</TableHead>
                  {showRequests && <TableHead className="text-right">Requests</TableHead>}
                  <TableHead>Platform</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead className="text-right">Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.rows.map(row => {
                  const email = row.key === null ? null : String(row.key)
                  return (
                    <TableRow
                      key={String(row.key)}
                      className="cursor-pointer"
                      onClick={() => onDrill({ email: email ? [email] : undefined })}
                    >
                      <TableCell className="max-w-[16rem] truncate font-medium text-foreground">
                        {email === '_magic_link' ? 'Anonymous link' : email ?? 'Unknown'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(row.interactions ?? 0).toLocaleString()}
                      </TableCell>
                      {showRequests && (
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {row.events.toLocaleString()}
                        </TableCell>
                      )}
                      <TableCell className="text-muted-foreground">
                        {email ? deviceByUser.get(email) ?? '—' : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {email ? countryByUser.get(email) ?? '—' : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-muted-foreground">
                        {relativeTime(row.lastSeen)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel title="Connection origins" hint="zoomed to the traffic, never past one country">
        <CardContent className="p-5">
          <Suspense fallback={
            <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: 340 }}>
              Loading map…
            </div>
          }>
            <OriginMap
              locations={locations.locations}
              loading={locations.loading}
              height={340}
              onSelectCountry={c => onDrill({ country: [c] })}
            />
          </Suspense>
        </CardContent>
      </Panel>
    </div>
  )
}

function GuidesView({ filters }: { filters: AnalyticsFilters }) {
  const { guides, sequences, loading } = useGuideAnalytics(filters)

  const maxWatch = Math.max(...sequences.map(s => s.watchedMs), 1)

  return (
    <div className="space-y-6">
      <Panel title="Guides" hint="who watched what, and for how long">
        {loading && guides.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : guides.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No guide activity recorded in this range.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Guide</TableHead>
                <TableHead>Seed</TableHead>
                <TableHead>Version</TableHead>
                <TableHead className="text-right">Opens</TableHead>
                <TableHead className="text-right">Viewers</TableHead>
                <TableHead className="text-right">Watch time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guides.map(g => (
                <TableRow key={`${g.seed}-${g.guideKey}-${g.guideVersion}-${g.revision}`}>
                  <TableCell className="font-medium text-foreground">{g.guideKey}</TableCell>
                  <TableCell className="text-muted-foreground">{g.seed ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {g.guideVersion ?? '—'}{g.revision !== null ? ` r${g.revision}` : ''}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{g.opens.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{g.uniqueViewers.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {(g.watchedMs / 60000).toFixed(1)} min
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {sequences.length > 0 && (
        <Panel title="Drop-off by sequence" hint="watch time per chapter">
          <CardContent className="space-y-2 p-5">
            {sequences.map(s => (
              <div key={`${s.guideKey}-${s.sequence}`} className="flex items-center gap-3">
                <span className="w-56 shrink-0 truncate text-sm text-muted-foreground">
                  {s.guideKey} · {s.sequence}
                </span>
                <span className="relative block h-2 flex-1 rounded-full bg-muted">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full bg-seed-green-500"
                    style={{ width: `${(s.watchedMs / maxWatch) * 100}%` }}
                  />
                </span>
                <span className="w-24 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {(s.watchedMs / 60000).toFixed(1)} min
                </span>
                <span className="w-20 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {s.viewers} viewers
                </span>
              </div>
            ))}
          </CardContent>
        </Panel>
      )}
    </div>
  )
}

function ExplorerView({ filters, options }: { filters: AnalyticsFilters; options: Options }) {
  const [kinds, setKinds] = useState<string[]>([])
  const scoped = useMemo(() => ({ ...filters, kind: kinds.length ? kinds : undefined }), [filters, kinds])
  const feed = useEventFeed(scoped, 100)

  const toggle = (kind: string) =>
    setKinds(prev => (prev.includes(kind) ? prev.filter(k => k !== kind) : [...prev, kind]))

  return (
    <Panel
      title="Raw events"
      hint={feed.exhausted ? `${feed.events.length} events — all of them` : `${feed.events.length} events loaded`}
    >
      <div className="flex flex-wrap gap-1.5 border-b px-5 py-3">
        {['nav', 'api', 'asset', 'platform', 'guide', 'auth', 'custom'].map(kind => (
          <Button
            key={kind}
            size="sm"
            variant={kinds.includes(kind) ? 'default' : 'outline'}
            onClick={() => toggle(kind)}
          >
            {kind}
          </Button>
        ))}
        {kinds.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setKinds([])}>Reset</Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Path</TableHead>
            <TableHead>App</TableHead>
            <TableHead>Share</TableHead>
            <TableHead>User</TableHead>
            <TableHead className="text-right">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {feed.events.map(e => (
            <TableRow key={e.id}>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {new Date(e.ts).toLocaleString()}
              </TableCell>
              <TableCell>
                <Badge variant={e.kind === 'api' ? 'outline' : 'secondary'}>{e.event_name || e.kind}</Badge>
              </TableCell>
              <TableCell className="max-w-[24rem] truncate font-mono text-xs text-foreground">{e.path}</TableCell>
              <TableCell className="text-muted-foreground">{e.app ?? '—'}</TableCell>
              <TableCell className="text-muted-foreground">
                {e.share_id === null
                  ? <span title="Recorded before share attribution existed">Unattributed</span>
                  : labelForShare(options.shareLabels, e.share_id)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {e.email === '_magic_link' ? 'Anonymous link' : e.email ?? '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">{e.status ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="border-t p-4 text-center">
        {feed.error && <p className="mb-2 text-sm text-destructive">{feed.error}</p>}
        {feed.exhausted
          ? <span className="text-sm text-muted-foreground">End of results</span>
          : <Button variant="outline" size="sm" disabled={feed.loading} onClick={feed.loadMore}>
              {feed.loading ? 'Loading…' : 'Load more'}
            </Button>}
      </div>
    </Panel>
  )
}
